import { useEffect, useId, useState, type FormEvent } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { agentActions, type AgentState } from "../store/agentSlice";
import { checkPages, MAX_PAGES, TIMED_OUT } from "./pages";

/**
 * A draft PDF's pages for the model to read itself, on its composer chip: the whole file until
 * some are picked. A PDF counted at more than `MAX_PAGES` pages (`pdfPages`), or too slow to count,
 * gets its first ones instead, unless pages were picked already, and never the whole file. Nothing
 * until it is counted (`useCountDraftPdfs`), so the whole file never shows for one that turns out
 * too long. Kept in this tab, and carried by the next send (`agentClient.send`); the server cuts
 * them to the PDF's own pages, for one too slow to count that turns out shorter.
 */
export function PagesPicker({
  threadId,
  fileId,
  name,
}: {
  threadId: number;
  fileId: number;
  name: string;
}) {
  const dispatch = useDispatch();
  const picked = useSelector(
    (state: { agent: AgentState }) => state.agent.pagePicks[threadId]?.[fileId] ?? null
  );
  // Undefined until counted; null when it could not be.
  const pageCount = useSelector((state: { agent: AgentState }) => state.agent.pdfPages[fileId]);
  const timedOut = pageCount === TIMED_OUT;
  const overCap = timedOut || (pageCount != null && pageCount > MAX_PAGES);
  // The default pick below lands an effect later: the label has it from the count's own render.
  const shown = picked ?? (overCap ? `1-${MAX_PAGES}` : null);
  const label = shown ? `Pages ${shown}` : "All pages";
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const inputId = useId();
  const problemId = useId();

  useEffect(() => {
    if (overCap && picked == null) {
      dispatch(agentActions.pagesPicked({ threadId, fileId, pages: `1-${MAX_PAGES}` }));
    }
  }, [dispatch, threadId, fileId, overCap, picked]);

  const checked = checkPages(text);
  const problem =
    "problem" in checked
      ? checked.problem
      : overCap && checked.pages == null
        ? timedOut
          ? `Pick up to ${MAX_PAGES} pages of this PDF.`
          : `This PDF has ${pageCount} pages: pick up to ${MAX_PAGES}.`
        : null;

  const pick = (pages: string | null) => {
    dispatch(agentActions.pagesPicked({ threadId, fileId, pages }));
    setOpen(false);
  };
  const apply = (event: FormEvent) => {
    event.preventDefault();
    if (!problem && !("problem" in checked)) pick(checked.pages);
  };

  if (pageCount === undefined) return null;
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setText(picked ?? "");
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 shrink-0 px-1.5 text-[11px] font-normal text-muted-foreground max-sm:h-9"
          aria-label={`${label} of ${name}`}
        >
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        aria-label={`Pages of ${name}`}
        className="w-64 p-3 max-sm:w-[calc(100vw-2rem)]"
      >
        <form onSubmit={apply} className="flex flex-col gap-2 text-xs">
          <label htmlFor={inputId} className="font-medium">
            Pages to send
          </label>
          <Input
            id={inputId}
            value={text}
            placeholder="e.g. 1-3, 7"
            autoComplete="off"
            aria-describedby={problem ? problemId : undefined}
            aria-invalid={problem !== null}
            className="h-8 text-xs"
            onChange={(event) => setText(event.target.value)}
          />
          {problem && (
            <p id={problemId} role="alert" className="text-destructive">
              {problem}
            </p>
          )}
          <div className="flex justify-end gap-2">
            {!overCap && (
              <Button type="button" variant="ghost" size="sm" onClick={() => pick(null)}>
                All pages
              </Button>
            )}
            {/* Disabled, so Enter in the field does not apply it either. */}
            <Button type="submit" size="sm" disabled={problem !== null}>
              Apply
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
